/**
 * SumUp settlement settings — ``/settings/sumup`` (Owner-only).
 *
 * One workspace covering the whole ``add-sumup-settlement`` operator
 * surface:
 *
 *   1. Global config — enable toggle, environment (with a prominent red
 *      LIVE warning), merchant code, the two write-only credential keys
 *      (``secret_key`` / ``affiliate_key``; "leave blank to keep
 *      existing" + a presence indicator), the two timeout windows, and a
 *      Test-connection button that names the environment in its result.
 *   2. Readers — global pairing (pair by code + a live list of paired
 *      readers); selection onto a surface lives in the policy matrix.
 *   3. Per-surface policy matrix — one card group per ``surface`` row
 *      (extensible by row server-side). Each group is split into
 *      Channels / Behaviour / Payment / User & Guest Rules sub-cards.
 *
 * Card payments are referenced server-side by the immutable
 * ``payment_method.code = "card"`` — nothing here touches the card
 * method by name. Secrets are never echoed back: a read exposes only the
 * ``has_secret_key`` / ``has_affiliate_key`` presence booleans.
 *
 * Sized for mixed touch + mouse: comfortable (not oversized) controls,
 * full-width number inputs, surfaces laid out responsively.
 */

import { useEffect, useRef, useState } from "react";
import {
  Alert,
  Button,
  Card,
  Col,
  Descriptions,
  Divider,
  Form,
  Input,
  Modal,
  Row,
  Select,
  Space,
  Switch,
  Tag,
  Typography,
  message,
} from "antd";
import { NumInput as InputNumber } from "../../components/NumInput";

import {
  ApiError,
  type SumupConfigOut,
  type SumupMerchantOption,
  type SumupReaderRow,
  type SumupSurfacePolicyRow,
  type SumupTestResult,
  type UpdateSumupConfigRequest,
  type UpdateSurfacePolicyRequest,
  api,
} from "../../api/client";

const { Title, Paragraph, Text } = Typography;

// SumUp brand blue — used only as a subtle accent (the test-connection
// CTA) so the page still reads as the Fluent admin shell.
const SUMUP_BLUE = "#0073E6";

/** Editable shape of a single surface row in local state. Mirrors
 * ``SumupSurfacePolicyRow`` 1:1 (every column is edited in place). */
type SurfaceDraft = SumupSurfacePolicyRow;

/** Local config-form state (scalars only; surfaces live separately so the
 * matrix can edit each row in place). Secrets are write-only inputs and
 * never seeded from the server. */
interface ConfigForm {
  // `enabled` is NOT a form field — it's a standalone immediate-action switch
  // (Save never enables; enabling is deliberate + live-confirmed). See the
  // Enabled switch + onToggleEnabled below.
  environment: "sandbox" | "live";
  merchant_code: string;
  settle_timeout_seconds: number;
  result_display_seconds: number;
  return_to_user_page: boolean;
  card_background_enabled: boolean;
  secret_key: string;
  affiliate_key: string;
}

function humanizeSurface(surface: string): string {
  // ``self_service`` → ``Self service``; ``operator`` → ``Operator``.
  const spaced = surface.replace(/_/g, " ");
  return spaced.charAt(0).toUpperCase() + spaced.slice(1);
}

export const SumupSettingsPage = () => {
  const [config, setConfig] = useState<SumupConfigOut | null>(null);
  const [surfaces, setSurfaces] = useState<SurfaceDraft[]>([]);
  const [readers, setReaders] = useState<SumupReaderRow[]>([]);
  // After a merchant/env change clears the per-surface readers, prompt the
  // operator to re-pick: the empty reader dropdowns blink + the matrix scrolls
  // into view, until every reader-offering surface has one again.
  const [readerPromptActive, setReaderPromptActive] = useState(false);
  const surfaceMatrixRef = useRef<HTMLDivElement>(null);
  const [merchants, setMerchants] = useState<SumupMerchantOption[]>([]);
  const [loadingMerchants, setLoadingMerchants] = useState(false);
  const [togglingEnabled, setTogglingEnabled] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [pairing, setPairing] = useState(false);
  const [loadingReaders, setLoadingReaders] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [testResult, setTestResult] = useState<SumupTestResult | null>(null);
  const [pairCode, setPairCode] = useState("");
  const [pairName, setPairName] = useState("");

  const [form] = Form.useForm<ConfigForm>();
  // Watch the environment so the LIVE warning banner reacts live as the
  // operator switches the select, before saving.
  const watchedEnv = Form.useWatch("environment", form);
  const watchedMerchant = Form.useWatch("merchant_code", form);
  const watchedSecret = Form.useWatch("secret_key", form);
  const watchedAffiliate = Form.useWatch("affiliate_key", form);

  // An UNSAVED sensitive change (environment / merchant / a freshly-typed secret
  // or affiliate key) force-disables on save — reflect that in the top-left
  // indicator IMMEDIATELY, so it doesn't keep claiming "Enabled" for the old
  // target while the operator re-points it. (The backend disables on save; this
  // is the optimistic mirror so the toggle never lies about the pending change.)
  const sensitiveDirty =
    !!config &&
    (watchedEnv !== config.environment ||
      (watchedMerchant ?? "") !== (config.merchant_code ?? "") ||
      !!watchedSecret ||
      !!watchedAffiliate);
  const effectiveEnabled = (config?.enabled ?? false) && !sensitiveDirty;

  const watchedTimeout = Form.useWatch("settle_timeout_seconds", form);
  const watchedResult = Form.useWatch("result_display_seconds", form);
  const watchedReturn = Form.useWatch("return_to_user_page", form);
  const watchedCardBg = Form.useWatch("card_background_enabled", form);
  // "Dirty" = the form/surfaces actually DIFFER from the saved config — DERIVED,
  // so reverting a change (e.g. Live, then back to Sandbox) clears it again,
  // instead of a one-way "touched" flag that left the Save button blinking on a
  // net-zero change. Blocks enabling a stale config + drives the Save pulse.
  const dirty =
    !!config &&
    (sensitiveDirty ||
      (watchedTimeout != null &&
        watchedTimeout !== config.settle_timeout_seconds) ||
      (watchedResult != null &&
        watchedResult !== config.result_display_seconds) ||
      (watchedReturn != null && watchedReturn !== config.return_to_user_page) ||
      (watchedCardBg != null &&
        watchedCardBg !== config.card_background_enabled) ||
      JSON.stringify(surfaces) !== JSON.stringify(config.surfaces));

  // Non-key dirtiness drives the debounced AUTO-SAVE: everything EXCEPT the
  // write-only secret/affiliate keys (those save on blur, so a half-typed key
  // can't be persisted or cleared mid-entry).
  const dirtyNonKey =
    !!config &&
    (watchedEnv !== config.environment ||
      (watchedMerchant ?? "") !== (config.merchant_code ?? "") ||
      (watchedTimeout != null && watchedTimeout !== config.settle_timeout_seconds) ||
      (watchedResult != null && watchedResult !== config.result_display_seconds) ||
      (watchedReturn != null && watchedReturn !== config.return_to_user_page) ||
      (watchedCardBg != null && watchedCardBg !== config.card_background_enabled) ||
      JSON.stringify(surfaces) !== JSON.stringify(config.surfaces));

  // Auto-save: persist any non-key change ~0.9s after the operator stops — no
  // Save button. The Enabled switch stays a separate, deliberate action (a LIVE
  // merchant prompts a confirm) and a sensitive change auto-disables on save, so
  // auto-saving a merchant/env switch can never silently go live.
  const autoSaveSnapshot = JSON.stringify({
    watchedEnv,
    watchedMerchant,
    watchedTimeout,
    watchedResult,
    watchedReturn,
    watchedCardBg,
    surfaces,
  });
  useEffect(() => {
    if (!config || !dirtyNonKey || saving) return;
    const t = window.setTimeout(() => void onSave({ auto: true }), 900);
    return () => window.clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoSaveSnapshot, dirtyNonKey, saving]);

  // Stop the reader-pick prompt once every reader-offering surface has one.
  useEffect(() => {
    if (readerPromptActive && !surfaces.some((s) => s.offer_reader && !s.reader_id)) {
      setReaderPromptActive(false);
    }
  }, [readerPromptActive, surfaces]);

  // Rapid blink for the empty reader dropdowns after a merchant/env change, to
  // draw the operator to re-pick a reader. Keyframes injected once.
  useEffect(() => {
    const id = "sumup-reader-blink-kf";
    if (document.getElementById(id)) return;
    const style = document.createElement("style");
    style.id = id;
    style.textContent =
      "@keyframes sumupReaderBlink{" +
      "0%,100%{box-shadow:0 0 0 0 rgba(250,173,20,0)}" +
      "50%{box-shadow:0 0 0 5px rgba(250,173,20,0.6)}}";
    document.head.appendChild(style);
  }, []);

  // The merchant dropdown is filtered to the selected environment via SumUp's
  // authoritative is_test_account flag, so picking a live merchant while in
  // sandbox (or vice versa) is structurally impossible.
  const wantTest = watchedEnv === "sandbox";
  const filteredMerchants = merchants.filter((m) => m.is_test_account === wantTest);
  const selectedMerchant = merchants.find((m) => m.merchant_code === watchedMerchant);

  // Merchants the stored key can reach (live + sandbox), with names + the test
  // flag. On-demand (a SumUp call) — auto-loaded on refresh when a key exists.
  const loadMerchants = async () => {
    setLoadingMerchants(true);
    try {
      setMerchants(await api.listSumupMerchants());
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoadingMerchants(false);
    }
  };

  // The Enabled switch is an IMMEDIATE action (not a Save field): toggling PUTs
  // {enabled} directly. Save never enables; changing env/merchant/key auto-
  // disables — re-enabling here is the deliberate step.
  const doSetEnabled = async (next: boolean) => {
    setTogglingEnabled(true);
    setError(null);
    try {
      const cfg = await api.updateSumupConfig({ enabled: next });
      setConfig(cfg);
      form.setFieldsValue({ merchant_code: cfg.merchant_code ?? "" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setTogglingEnabled(false);
    }
  };

  const onToggleEnabled = (next: boolean) => {
    if (!next) {
      void doSetEnabled(false); // disabling is always immediate + safe
      return;
    }
    if (dirty) {
      message.warning("Save your configuration changes before enabling.");
      return;
    }
    const merchant: string = form.getFieldValue("merchant_code");
    if (!merchant) {
      message.warning("Select a merchant before enabling.");
      return;
    }
    const sel = merchants.find((m) => m.merchant_code === merchant);
    const isLive = sel ? !sel.is_test_account : true; // unknown → treat as live
    if (!isLive) {
      void doSetEnabled(true);
      return;
    }
    Modal.confirm({
      title: "Enable LIVE SumUp payments?",
      content: `Merchant ${merchant} is a LIVE account — real cards will be charged. Make sure this is correct before enabling.`,
      okText: "Enable (real money)",
      okType: "danger",
      cancelText: "Cancel",
      onOk: () => doSetEnabled(true),
    });
  };

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const cfg = await api.getSumupConfig();
      setConfig(cfg);
      setSurfaces(cfg.surfaces.map((s) => ({ ...s })));
      form.setFieldsValue({
        environment: cfg.environment,
        merchant_code: cfg.merchant_code ?? "",
        settle_timeout_seconds: cfg.settle_timeout_seconds,
        result_display_seconds: cfg.result_display_seconds,
        return_to_user_page: cfg.return_to_user_page,
        card_background_enabled: cfg.card_background_enabled,
        // Write-only — always start blank ("leave blank to keep existing").
        secret_key: "",
        affiliate_key: "",
      });
      // Populate the merchant dropdown from the key's memberships (live +
      // sandbox); only possible once a key is stored.
      if (cfg.has_secret_key) void loadMerchants();
      // Reconcile on load too, so a PRE-EXISTING stale reader_id (e.g. left over
      // from an earlier env/merchant) is dropped before any settlement attempt.
      // Guarded like onSave so an unreachable config doesn't stomp the page.
      if (cfg.has_secret_key && cfg.merchant_code) void loadReaders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadReaders = async () => {
    setLoadingReaders(true);
    setError(null);
    try {
      const rows = await api.listSumupReaders();
      setReaders(rows);
      reconcileSurfaceReaders(rows);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setLoadingReaders(false);
    }
  };

  // Reconcile each surface's reader against the merchant's actually-paired
  // readers: DROP a reader_id that isn't paired with the active merchant (e.g.
  // a reader left over after switching environment/merchant — it would 404 at
  // arm time and silently break settlement), and AUTO-SELECT the sole reader
  // when exactly one is paired (mirrors the merchant auto-pick). Any change
  // marks the form dirty so the operator saves the corrected selection.
  const reconcileSurfaceReaders = (rows: SumupReaderRow[]) => {
    const ids = new Set(rows.map((r) => r.id));
    const only = rows.length === 1 ? rows[0].id : null;
    setSurfaces((prev) =>
      prev.map((s) => {
        let reader_id = s.reader_id;
        if (reader_id && !ids.has(reader_id)) reader_id = null; // stale → clear
        if (!reader_id && only) reader_id = only; // exactly one → auto-select
        return reader_id === s.reader_id ? s : { ...s, reader_id };
      }),
    );
  };

  // Drop every surface's reader_id — used the instant the merchant/environment
  // selection changes, because a reader paired with the OLD merchant won't exist
  // on the new one (it would 404 at arm time). The sole paired reader is then
  // re-selected by reconcileSurfaceReaders on the next reader load.
  const clearSurfaceReaders = () => {
    setSurfaces((prev) =>
      prev.map((s) => (s.reader_id ? { ...s, reader_id: null } : s)),
    );
    // Prompt the operator to re-pick: blink the now-empty reader dropdowns and
    // scroll the surface matrix into view (cleared by the effect once each is
    // filled, or auto-picked when the new merchant has exactly one reader).
    setReaderPromptActive(true);
    window.requestAnimationFrame(() =>
      surfaceMatrixRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }),
    );
  };

  const patchSurface = (
    surface: string,
    patch: Partial<SurfaceDraft>,
  ) => {
    setSurfaces((prev) =>
      prev.map((s) => (s.surface === surface ? { ...s, ...patch } : s)),
    );
  };

  const onSave = async (opts?: { auto?: boolean; includeKeys?: boolean }) => {
    let values: ConfigForm;
    try {
      values = await form.validateFields();
    } catch {
      return; // invalid form — skip (a debounced auto-save will retry)
    }
    setSaving(true);
    setError(null);
    if (!opts?.auto) setInfo(null);
    try {
      const payload: UpdateSumupConfigRequest = {
        environment: values.environment,
        merchant_code: values.merchant_code.trim() || "",
        settle_timeout_seconds: values.settle_timeout_seconds,
        result_display_seconds: values.result_display_seconds,
        return_to_user_page: values.return_to_user_page,
        card_background_enabled: values.card_background_enabled,
        surfaces: surfaces.map(
          (s): UpdateSurfacePolicyRequest => ({
            surface: s.surface,
            offer_qr: s.offer_qr,
            offer_reader: s.offer_reader,
            presentation: s.presentation,
            allow_partial: s.allow_partial,
            min_partial_cents: s.min_partial_cents,
            require_confirm: s.require_confirm,
            reader_id: s.reader_id,
            reader_label: s.reader_label,
            disable_guest_after_full: s.disable_guest_after_full,
            direction_policy: s.direction_policy,
            topup_max_cents: s.topup_max_cents,
            guests_only: s.guests_only,
            non_guest_message: s.non_guest_message,
          }),
        ),
      };
      // Write-only secrets: ONLY an explicit key-save (the key field's blur, or
      // a manual save) sends them — never a debounced auto-save, so a half-typed
      // key can't be persisted or cleared mid-entry. Blank == "keep existing".
      if (opts?.includeKeys) {
        if (values.secret_key) payload.secret_key = values.secret_key;
        if (values.affiliate_key) payload.affiliate_key = values.affiliate_key;
      }

      const cfg = await api.updateSumupConfig(payload);
      setConfig(cfg);
      setSurfaces(cfg.surfaces.map((s) => ({ ...s })));
      form.setFieldsValue({
        environment: cfg.environment,
        merchant_code: cfg.merchant_code ?? "",
        settle_timeout_seconds: cfg.settle_timeout_seconds,
        result_display_seconds: cfg.result_display_seconds,
        return_to_user_page: cfg.return_to_user_page,
        card_background_enabled: cfg.card_background_enabled,
        // Only clear the write-only key fields when we actually sent them, so a
        // concurrent auto-save can't wipe a key the operator is mid-typing.
        ...(opts?.includeKeys ? { secret_key: "", affiliate_key: "" } : {}),
      });
      if (!opts?.auto) setInfo("Saved.");
      // Refresh the paired readers after a save (the merchant/key may have
      // changed). Only when the config can actually reach SumUp, so a sensitive
      // save (no merchant/key) doesn't replace "Saved." with a 503.
      if (cfg.merchant_code && cfg.has_secret_key) void loadReaders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const onClearSecret = async (which: "secret_key" | "affiliate_key") => {
    setSaving(true);
    setError(null);
    setInfo(null);
    try {
      const cfg = await api.updateSumupConfig({ [which]: "" });
      setConfig(cfg);
      setInfo(
        which === "secret_key"
          ? "Secret key cleared."
          : "Affiliate key cleared.",
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  const onTest = async () => {
    setTesting(true);
    setError(null);
    setTestResult(null);
    try {
      const result = await api.testSumup();
      setTestResult(result);
      // Refresh so the persisted last_test_at / last_test_status show, AND so an
      // auto-detected merchant code (filled server-side when left blank) appears
      // in the field.
      const cfg = await api.getSumupConfig();
      setConfig(cfg);
      form.setFieldsValue({ merchant_code: cfg.merchant_code ?? "" });
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setTesting(false);
    }
  };

  const onPair = async () => {
    const code = pairCode.trim();
    if (!code) {
      setError("Enter a pairing code.");
      return;
    }
    setPairing(true);
    setError(null);
    setInfo(null);
    try {
      const reader = await api.pairSumupReader({
        code,
        name: pairName.trim() || null,
      });
      setInfo(`Paired reader ${reader.name ?? reader.id}.`);
      setPairCode("");
      setPairName("");
      await loadReaders();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : String(err));
    } finally {
      setPairing(false);
    }
  };

  // Reader options for the per-surface "Payment" selector. The label is
  // what we persist into ``reader_label`` so the surface can show a name
  // even when the live list isn't loaded. Plus an explicit "none" option.
  const readerOptions = readers.map((r) => ({
    value: r.id,
    label: r.name ? `${r.name} (${r.id})` : r.id,
  }));

  const isLive = watchedEnv === "live";

  return (
    <Card loading={loading}>
      <Title level={2} style={{ marginBottom: 8 }}>
        SumUp card settlement
      </Title>
      <Paragraph type="secondary">
        Card settlement via SumUp — a QR hosted checkout and/or a paired
        card reader. Settled payments cross into the existing settlement
        machine using the immutable card payment method. Configure the
        global credentials below, pair your readers, then set per-surface
        behaviour for the self-service kiosk and the operator surface.
      </Paragraph>

      {error && (
        <Alert
          type="error"
          message={error}
          showIcon
          closable
          onClose={() => setError(null)}
          style={{ marginBottom: 16 }}
        />
      )}
      {info && (
        <Alert
          type="success"
          message={info}
          showIcon
          closable
          onClose={() => setInfo(null)}
          style={{ marginBottom: 16 }}
        />
      )}

      {/* ---- Global config ---- */}
      <Card
        type="inner"
        title="Global configuration"
        style={{ marginBottom: 16 }}
      >
        {isLive && (
          <Alert
            type="error"
            banner
            showIcon
            message="LIVE environment — real cards will be charged"
            description="Every settlement made through SumUp will move real money. Double-check the merchant code and credentials before enabling."
            style={{ marginBottom: 16 }}
          />
        )}
        <Form<ConfigForm>
          form={form}
          layout="vertical"
          initialValues={{
            environment: "sandbox",
            merchant_code: "",
            settle_timeout_seconds: 120,
            result_display_seconds: 15,
            return_to_user_page: true,
            card_background_enabled: true,
            secret_key: "",
            affiliate_key: "",
          }}
        >
          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Enabled"
                tooltip="Master switch (applies immediately). Enabling needs a saved config + a selected merchant; a LIVE merchant asks to confirm. Changing the environment, merchant, or key auto-disables — re-enable here."
              >
                <Space>
                  <Switch
                    checked={effectiveEnabled}
                    loading={togglingEnabled}
                    onChange={onToggleEnabled}
                  />
                  <Tag color={effectiveEnabled ? "green" : "default"}>
                    {effectiveEnabled ? "Enabled" : "Disabled"}
                  </Tag>
                  {sensitiveDirty && config?.enabled && (
                    <Tag color="orange">disables on save</Tag>
                  )}
                </Space>
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Environment"
                name="environment"
                rules={[{ required: true }]}
              >
                <Select
                  onChange={(env) => {
                    // When the selected merchant no longer matches the chosen
                    // environment (e.g. switching to Live with a sandbox
                    // merchant), auto-pick the new environment's merchant ONLY
                    // when there's exactly one — otherwise clear it so the
                    // operator deliberately picks (no silent re-targeting; a
                    // sandbox key reaches the live account too). The env change
                    // already auto-disables, so a LIVE pick still goes through
                    // the red warning + confirm modal before re-enabling.
                    const wantTest = env === "sandbox";
                    const sel = merchants.find(
                      (m) => m.merchant_code === form.getFieldValue("merchant_code"),
                    );
                    if (!sel || sel.is_test_account !== wantTest) {
                      const matches = merchants.filter(
                        (m) => m.is_test_account === wantTest,
                      );
                      form.setFieldsValue({
                        merchant_code:
                          matches.length === 1 ? matches[0].merchant_code : "",
                      });
                      // The merchant changed → its readers are merchant-specific,
                      // so drop stale per-surface readers (re-picked on next load).
                      clearSurfaceReaders();
                    }
                  }}
                  options={[
                    { value: "sandbox", label: "Sandbox (test)" },
                    { value: "live", label: "Live (real money)" },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label={
                  <Space size={4}>
                    Merchant
                    <Button
                      type="link"
                      size="small"
                      style={{ padding: 0, height: "auto" }}
                      loading={loadingMerchants}
                      onClick={() => void loadMerchants()}
                    >
                      {merchants.length ? "Refresh" : "Load from key"}
                    </Button>
                  </Space>
                }
                name="merchant_code"
                tooltip="Pick the SumUp merchant by name. The list is loaded from your key's memberships and filtered to the selected environment (sandbox merchants when Sandbox, live merchants when Live)."
              >
                <Select
                  showSearch
                  allowClear
                  optionFilterProp="label"
                  // Picking a different merchant invalidates the per-surface
                  // readers (they belong to the previous merchant) — clear them.
                  onChange={() => clearSurfaceReaders()}
                  placeholder={
                    config?.has_secret_key
                      ? "Pick a merchant…"
                      : "Save a key first, then load"
                  }
                  notFoundContent={
                    loadingMerchants
                      ? "Loading…"
                      : merchants.length
                        ? `No ${wantTest ? "sandbox" : "live"} merchant for this key`
                        : "Click Load (needs a saved key)"
                  }
                  options={filteredMerchants.map((m) => ({
                    value: m.merchant_code,
                    // NOTE: SumUp's /memberships resource.name is intermittently
                    // blank for these accounts even though the dashboard shows the
                    // name (a SumUp-side quirk) — keep the simple fallback; it
                    // repopulates when SumUp does. Test-vs-live stays visible via
                    // the Environment field + the green/red TEST/LIVE tag below.
                    label: `${m.name || "(unnamed)"} · ${m.merchant_code}`,
                  }))}
                />
              </Form.Item>
              {selectedMerchant && (
                <Tag
                  color={selectedMerchant.is_test_account ? "green" : "red"}
                  style={{ marginTop: -8 }}
                >
                  {selectedMerchant.is_test_account
                    ? "TEST account"
                    : "LIVE — real money"}
                </Tag>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <Space size={4}>
                    Secret key
                    {config && (
                      <Tag color={config.has_secret_key ? "green" : "default"}>
                        {config.has_secret_key ? "stored" : "not set"}
                      </Tag>
                    )}
                  </Space>
                }
                name="secret_key"
                tooltip="SumUp API secret key. Write-only — leave blank to keep the existing key."
                extra="Leave blank to keep existing."
                rules={[{ max: 2048, message: "At most 2048 characters." }]}
              >
                <Input.Password
                  placeholder="•••••••• (leave blank to keep existing)"
                  autoComplete="new-password"
                  onBlur={() => {
                    // Keys save on blur (complete), excluded from the debounced
                    // auto-save so a half-typed key can't be persisted/cleared.
                    if (form.getFieldValue("secret_key"))
                      void onSave({ auto: true, includeKeys: true });
                  }}
                />
              </Form.Item>
              {config?.has_secret_key && (
                <Button
                  size="small"
                  danger
                  type="text"
                  disabled={saving}
                  onClick={() => void onClearSecret("secret_key")}
                  style={{ marginTop: -8, marginBottom: 8 }}
                >
                  Clear stored secret key
                </Button>
              )}
            </Col>
            <Col xs={24} md={12}>
              <Form.Item
                label={
                  <Space size={4}>
                    Affiliate key
                    {config && (
                      <Tag
                        color={config.has_affiliate_key ? "green" : "default"}
                      >
                        {config.has_affiliate_key ? "stored" : "not set"}
                      </Tag>
                    )}
                  </Space>
                }
                name="affiliate_key"
                tooltip="SumUp affiliate (application) key, if your setup requires one. Write-only — leave blank to keep the existing key."
                extra="Leave blank to keep existing."
                rules={[{ max: 2048, message: "At most 2048 characters." }]}
              >
                <Input.Password
                  placeholder="•••••••• (leave blank to keep existing)"
                  autoComplete="new-password"
                  onBlur={() => {
                    if (form.getFieldValue("affiliate_key"))
                      void onSave({ auto: true, includeKeys: true });
                  }}
                />
              </Form.Item>
              {config?.has_affiliate_key && (
                <Button
                  size="small"
                  danger
                  type="text"
                  disabled={saving}
                  onClick={() => void onClearSecret("affiliate_key")}
                  style={{ marginTop: -8, marginBottom: 8 }}
                >
                  Clear stored affiliate key
                </Button>
              )}
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Settle timeout (seconds)"
                name="settle_timeout_seconds"
                tooltip="Kiosk inactivity window — clears the screen. Does NOT cancel an in-flight QR payment."
                rules={[{ required: true, message: "Required." }]}
              >
                <InputNumber
                  min={1}
                  max={3600}
                  step={1}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Result display (seconds)"
                name="result_display_seconds"
                tooltip="How long the terminal result screen (success / failure) stays up before auto-closing."
                rules={[{ required: true, message: "Required." }]}
              >
                <InputNumber
                  min={1}
                  max={3600}
                  step={1}
                  style={{ width: "100%" }}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Return to user page after settle"
                name="return_to_user_page"
                valuePropName="checked"
                tooltip="Kiosk only. When on, closing the settle flow (cancel / timeout / finish) returns to the payer's own page so they see their updated/zeroed balance. When off, it goes to the front page. A guest auto-disabled by the payment always goes to the front page."
              >
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Form.Item
                label="Background POS card payments"
                name="card_background_enabled"
                valuePropName="checked"
                tooltip="POS only. When ON, arming a card parks the order and frees the till — the card runs in the background (a blinking pill). When OFF, the card payment is foreground/blocking: a window stays up until the card is paid, declined, or the operator takes cash / cancels, and the till is not freed mid-payment."
              >
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Space wrap>
            <Text type={saving ? "secondary" : dirty ? "warning" : "success"}>
              {saving
                ? "Saving…"
                : dirty
                  ? "Saving changes…"
                  : config
                    ? "✓ All changes saved"
                    : ""}
            </Text>
            {dirty && !saving && (
              <Button
                type="link"
                size="small"
                onClick={() => void onSave({ includeKeys: true })}
              >
                Save now
              </Button>
            )}
            <Button disabled={saving} onClick={() => void refresh()}>
              Reset
            </Button>
            <Button
              loading={testing}
              onClick={() => void onTest()}
              style={{ borderColor: SUMUP_BLUE, color: SUMUP_BLUE }}
            >
              Test connection
            </Button>
          </Space>
        </Form>

        {testResult && (
          <Alert
            type={testResult.ok ? "success" : "warning"}
            showIcon
            closable
            onClose={() => setTestResult(null)}
            style={{ marginTop: 16 }}
            message={
              testResult.ok
                ? `Connected to SumUp (${testResult.environment})`
                : `Could not connect (${testResult.environment})`
            }
            description={testResult.message}
          />
        )}

        {config && (
          <Descriptions
            size="small"
            column={2}
            style={{ marginTop: 16 }}
          >
            <Descriptions.Item label="Last test">
              {config.last_test_at
                ? new Date(config.last_test_at).toLocaleString()
                : "—"}
            </Descriptions.Item>
            <Descriptions.Item label="Last test status">
              {config.last_test_status ?? "—"}
            </Descriptions.Item>
          </Descriptions>
        )}
      </Card>

      {/* ---- Readers (global pairing) ---- */}
      <Card
        type="inner"
        title="Card readers"
        extra={
          <Button
            size="small"
            loading={loadingReaders}
            onClick={() => void loadReaders()}
          >
            Refresh list
          </Button>
        }
        style={{ marginBottom: 16 }}
      >
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          Pairing is global. Selecting which reader a surface uses happens
          in the per-surface Payment card below.
        </Paragraph>
        <Space wrap align="end" style={{ marginBottom: 16 }}>
          <div>
            <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
              Pairing code
            </Text>
            <Input
              placeholder="e.g. ABCD1234"
              value={pairCode}
              onChange={(e) => setPairCode(e.target.value)}
              style={{ width: 200 }}
              maxLength={64}
            />
          </div>
          <div>
            <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
              Reader name (optional)
            </Text>
            <Input
              placeholder="e.g. Bar reader"
              value={pairName}
              onChange={(e) => setPairName(e.target.value)}
              style={{ width: 200 }}
              maxLength={128}
            />
          </div>
          <Button type="primary" loading={pairing} onClick={() => void onPair()}>
            Pair reader
          </Button>
        </Space>

        {readers.length > 0 ? (
          <Space direction="vertical" style={{ width: "100%" }} size={8}>
            {readers.map((r) => (
              <Card key={r.id} size="small">
                <Space size="middle" wrap>
                  <Text strong>{r.name ?? "(unnamed)"}</Text>
                  <Text type="secondary" code>
                    {r.id}
                  </Text>
                  {r.status && <Tag>{r.status}</Tag>}
                </Space>
              </Card>
            ))}
          </Space>
        ) : (
          <Text type="secondary">
            No readers loaded. Click "Refresh list" to fetch paired readers,
            or pair one above.
          </Text>
        )}
      </Card>

      {/* ---- Per-surface policy matrix ---- */}
      <div ref={surfaceMatrixRef}>
        <Title level={4} style={{ marginTop: 8 }}>
          Per-surface policy
        </Title>
      </div>
      <Paragraph type="secondary">
        Behaviour per surface — the self-service kiosk and the operator
        surface. The orchestrator branches on these columns, never on the
        surface name, so new surfaces can be added server-side.
      </Paragraph>

      {surfaces.length === 0 && !loading && (
        <Text type="secondary">No surface policies configured.</Text>
      )}

      <Space direction="vertical" style={{ width: "100%" }} size={16}>
        {surfaces.map((s) => (
          <Card
            key={s.surface}
            title={
              <Space>
                {humanizeSurface(s.surface)}
                <Text type="secondary" code style={{ fontSize: 12 }}>
                  {s.surface}
                </Text>
              </Space>
            }
          >
            <Row gutter={[16, 16]}>
              {/* Channels */}
              <Col xs={24} md={12} xl={6}>
                <Card type="inner" title="Channels" size="small">
                  <Space direction="vertical" style={{ width: "100%" }} size={12}>
                    <Space style={{ justifyContent: "space-between", width: "100%" }}>
                      <span>Offer QR</span>
                      <Switch
                        checked={s.offer_qr}
                        onChange={(v) => patchSurface(s.surface, { offer_qr: v })}
                      />
                    </Space>
                    <Space style={{ justifyContent: "space-between", width: "100%" }}>
                      <span>Offer reader</span>
                      <Switch
                        checked={s.offer_reader}
                        onChange={(v) =>
                          patchSurface(s.surface, { offer_reader: v })
                        }
                      />
                    </Space>
                    <div>
                      <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                        Presentation
                      </Text>
                      <Select<SumupSurfacePolicyRow["presentation"]>
                        value={s.presentation}
                        style={{ width: "100%" }}
                        onChange={(v) =>
                          patchSurface(s.surface, { presentation: v })
                        }
                        options={[
                          { value: "both_live", label: "Both live (first paid wins)" },
                          { value: "user_selects", label: "User selects channel" },
                        ]}
                      />
                    </div>
                  </Space>
                </Card>
              </Col>

              {/* Behaviour */}
              <Col xs={24} md={12} xl={6}>
                <Card type="inner" title="Behaviour" size="small">
                  <Space direction="vertical" style={{ width: "100%" }} size={12}>
                    <Space style={{ justifyContent: "space-between", width: "100%" }}>
                      <span>Allow partial</span>
                      <Switch
                        checked={s.allow_partial}
                        onChange={(v) =>
                          patchSurface(s.surface, { allow_partial: v })
                        }
                      />
                    </Space>
                    <div>
                      <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                        Minimum charge (cents)
                      </Text>
                      <InputNumber
                        min={0}
                        step={1}
                        value={s.min_partial_cents}
                        style={{ width: "100%" }}
                        onChange={(v) =>
                          patchSurface(s.surface, {
                            min_partial_cents: v ?? 0,
                          })
                        }
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Floored at SumUp's €1.00 minimum at charge time.
                      </Text>
                    </div>
                    <Space style={{ justifyContent: "space-between", width: "100%" }}>
                      <span>Require confirm</span>
                      <Switch
                        checked={s.require_confirm}
                        onChange={(v) =>
                          patchSurface(s.surface, { require_confirm: v })
                        }
                      />
                    </Space>
                  </Space>
                </Card>
              </Col>

              {/* Payment (reader selection) */}
              <Col xs={24} md={12} xl={6}>
                <Card type="inner" title="Payment" size="small">
                  <Space direction="vertical" style={{ width: "100%" }} size={12}>
                    <div>
                      <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                        Reader
                      </Text>
                      <Select
                        value={s.reader_id ?? undefined}
                        status={
                          readerPromptActive && s.offer_reader && !s.reader_id
                            ? "warning"
                            : undefined
                        }
                        style={{
                          width: "100%",
                          ...(readerPromptActive && s.offer_reader && !s.reader_id
                            ? {
                                animation: "sumupReaderBlink 0.7s ease-in-out infinite",
                                borderRadius: 8,
                              }
                            : {}),
                        }}
                        placeholder="None (QR / online only)"
                        allowClear
                        showSearch
                        optionFilterProp="label"
                        notFoundContent={
                          readers.length === 0
                            ? "Refresh the reader list above"
                            : undefined
                        }
                        onChange={(v) => {
                          const picked = readers.find((r) => r.id === v);
                          patchSurface(s.surface, {
                            reader_id: v ?? null,
                            // Mirror the chosen reader's name into the label so
                            // the surface keeps a human name even offline.
                            reader_label: v
                              ? (picked?.name ?? s.reader_label ?? null)
                              : null,
                          });
                        }}
                        options={readerOptions}
                      />
                    </div>
                    <div>
                      <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                        Reader label
                      </Text>
                      <Input
                        value={s.reader_label ?? ""}
                        placeholder="Shown on this surface"
                        maxLength={128}
                        onChange={(e) =>
                          patchSurface(s.surface, {
                            reader_label: e.target.value || null,
                          })
                        }
                      />
                    </div>
                  </Space>
                </Card>
              </Col>

              {/* User & Guest Rules */}
              <Col xs={24} md={12} xl={6}>
                <Card type="inner" title="User & Guest Rules" size="small">
                  <Space direction="vertical" style={{ width: "100%" }} size={12}>
                    <Space style={{ justifyContent: "space-between", width: "100%" }}>
                      <span>Disable guest after full settle</span>
                      <Switch
                        checked={s.disable_guest_after_full}
                        onChange={(v) =>
                          patchSurface(s.surface, {
                            disable_guest_after_full: v,
                          })
                        }
                      />
                    </Space>
                    <Space style={{ justifyContent: "space-between", width: "100%" }}>
                      <span>Guests only (block regular members)</span>
                      <Switch
                        checked={s.guests_only}
                        onChange={(v) =>
                          patchSurface(s.surface, { guests_only: v })
                        }
                      />
                    </Space>
                    {s.guests_only && (
                      <div>
                        <Text
                          type="secondary"
                          style={{ display: "block", fontSize: 12 }}
                        >
                          Non-guest message
                        </Text>
                        <Input
                          value={s.non_guest_message ?? ""}
                          placeholder="Please pay at the office/crew-care on check-out"
                          maxLength={255}
                          onChange={(e) =>
                            patchSurface(s.surface, {
                              non_guest_message: e.target.value || null,
                            })
                          }
                        />
                      </div>
                    )}
                    <div>
                      <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                        Direction policy
                      </Text>
                      <Select<SumupSurfacePolicyRow["direction_policy"]>
                        value={s.direction_policy}
                        style={{ width: "100%" }}
                        onChange={(v) =>
                          patchSurface(s.surface, { direction_policy: v })
                        }
                        options={[
                          {
                            value: "repayment_only",
                            label: "Repayment only (user → bank)",
                          },
                          { value: "both", label: "Both directions" },
                        ]}
                      />
                    </div>
                    <div>
                      <Text type="secondary" style={{ display: "block", fontSize: 12 }}>
                        Top-up max (cents)
                      </Text>
                      <InputNumber
                        min={0}
                        step={100}
                        value={s.topup_max_cents}
                        disabled={s.direction_policy !== "both"}
                        style={{ width: "100%" }}
                        onChange={(v) =>
                          patchSurface(s.surface, { topup_max_cents: v ?? 0 })
                        }
                      />
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        Max a charge may exceed the debt, carrying the user into
                        credit. Only with “Both directions”.
                      </Text>
                    </div>
                  </Space>
                </Card>
              </Col>
            </Row>
          </Card>
        ))}
      </Space>

      <Divider />
      <Space>
        <Text type={saving ? "secondary" : dirty ? "warning" : "success"}>
          {saving
            ? "Saving…"
            : dirty
              ? "Saving changes…"
              : config
                ? "✓ All changes saved"
                : ""}
        </Text>
        {dirty && !saving && (
          <Button
            type="link"
            size="small"
            onClick={() => void onSave({ includeKeys: true })}
          >
            Save now
          </Button>
        )}
        <Button disabled={saving} onClick={() => void refresh()}>
          Reset
        </Button>
      </Space>
    </Card>
  );
};

export default SumupSettingsPage;
